import React, { createContext, useState, useCallback, ReactNode } from 'react';

interface ILoaderContextType {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    loaderMessage: string;
    showLoader: (message?: string) => void;
    hideLoader: () => void;
    setLoaderMessage: (message: string) => void;
}

const LoaderContext = createContext<ILoaderContextType | undefined>(undefined);

interface ILoaderProviderProps {
    children: ReactNode;
}

const LoaderProvider = ({ children}: ILoaderProviderProps) => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loaderMessage, setLoaderMessage] = useState<string>('Loading...');
    const [, setLoaderCount] = useState<number>(0);

    const showLoader = useCallback((message: string = 'Loading...') => {
        setLoaderCount(prev => prev + 1);
        setLoaderMessage(message);
        setIsLoading(true);
    }, []);

    const hideLoader = useCallback(() => {
        setLoaderCount(prev => {
            const newCount = Math.max(0, prev - 1);
            if (newCount === 0) {
                setIsLoading(false);
            }
            return newCount;
        });
    }, []);

    const updateLoaderMessage = useCallback((message: string) => {
        setLoaderMessage(message);
    }, []);

    const payload = {
        isLoading,
        setIsLoading,
        loaderMessage,
        showLoader,
        hideLoader,
        setLoaderMessage: updateLoaderMessage,
    }

    return (
        <LoaderContext.Provider
            value={payload}>
            {children}
        </LoaderContext.Provider>
    );
};

const useLoader = () => {
    const context = React.useContext(LoaderContext);
    if (!context) {
        throw new Error('useLoader must be used within a LoaderProvider');
    }
    return context;
};

export { LoaderProvider, LoaderContext, useLoader };
